import React, { useCallback, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Password field with a persistent show/hide eye toggle.
 * Same public API as <Input/>.
 *
 * Mobile-safe: the eye button sits above the input's right edge but the toggle
 *   1) does NOT steal focus from the input on tap (mousedown/touchstart
 *      preventDefault — standard pattern for iOS Safari), and
 *   2) DOES focus the input on tap when the input is not yet focused, so any
 *      tap anywhere on the visible password field reliably opens the on-screen
 *      keyboard (the previous behaviour left users unable to enter a password
 *      when they happened to tap the right edge first).
 */
const PasswordInput = forwardRef(({ className, onFocus, ...props }, ref) => {
  const [show, setShow] = useState(false);
  const inputRef = useRef(null);
  useImperativeHandle(ref, () => inputRef.current, []);

  const toggleShow = useCallback(() => {
    setShow((s) => !s);
    // If the input isn't already focused, focus it so the mobile keyboard
    // opens even when the user tapped the eye first.
    const el = inputRef.current;
    if (el && document.activeElement !== el) {
      // Use rAF so the focus() call happens after the click's default focus
      // shuffle is over — otherwise iOS drops it silently.
      requestAnimationFrame(() => el.focus());
    }
  }, []);

  // Prevent the button from stealing focus from the input while the user is
  // typing (a classic iOS Safari gotcha). `mousedown` covers desktop mice;
  // `touchstart` covers real touch. `pointerdown` would collapse them but has
  // slightly weaker Safari coverage than the explicit pair.
  const preventFocusSteal = (e) => {
    if (document.activeElement === inputRef.current) {
      e.preventDefault();
    }
  };

  return (
    <div className="relative" style={{ touchAction: "manipulation" }}>
      <Input
        ref={inputRef}
        type={show ? "text" : "password"}
        onFocus={onFocus}
        className={cn("pr-11", className)}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={toggleShow}
        onMouseDown={preventFocusSteal}
        onTouchStart={preventFocusSteal}
        aria-label={show ? "Hide password" : "Show password"}
        title={show ? "Hide password" : "Show password"}
        data-testid="password-eye-toggle"
        className="absolute right-1 top-1/2 -translate-y-1/2 inline-flex h-9 w-9 items-center justify-center rounded-md text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
      >
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
});
PasswordInput.displayName = "PasswordInput";

export default PasswordInput;
